import React from "react";
import {TextField, ToggleButton, ToggleButtonGroup} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import NewColorPicker from "crqlar-react-common/dist/components/NewColorPicker";
import {printoutPalette} from "../../../core/printoutPalette";
import {
    clampDividerThickness,
    DIVIDER_THICKNESS_OPTIONS,
} from "../../../blocks/divider/printoutDividerOptions";
import {PanelField} from "../PrintoutPanelSection";
import {updateSelectedPrintoutBlockAttributes} from "./printoutBlockAttributes";
import type {PrintoutBlockSettingsProps} from "./printoutBlockAttributes";

export function DividerSettings({block, disabled, editor}: PrintoutBlockSettingsProps) {
    const {t} = useTranslation();
    const theme = useTheme();

    const color = typeof block.node.attrs.color === 'string' ? block.node.attrs.color : null;
    const label = typeof block.node.attrs.label === 'string' ? block.node.attrs.label : '';
    const thickness = clampDividerThickness(block.node.attrs.thickness);

    const update = (attrs: Record<string, unknown>) =>
        updateSelectedPrintoutBlockAttributes(editor, block, attrs);

    const colorPresets = [
        printoutPalette.mutedText,
        printoutPalette.primaryText,
        theme.palette.primary.main,
        printoutPalette.subtleBorder,
    ];

    return (
        <>
            <PanelField label={t('Label')}>
                <TextField
                    disabled={disabled}
                    fullWidth
                    // Preserve spaces while typing; rendering trims the final label.
                    onChange={(event) => update({
                        label: event.target.value.trim() ? event.target.value : null,
                    })}
                    placeholder={t('No label yet')}
                    size={'small'}
                    value={label}
                />
            </PanelField>

            <PanelField label={t('Thickness')}>
                <ToggleButtonGroup
                    disabled={disabled}
                    exclusive
                    fullWidth
                    onChange={(_event, value: number | null) => {
                        if (value) update({thickness: value});
                    }}
                    size={'small'}
                    value={thickness}
                >
                    {DIVIDER_THICKNESS_OPTIONS.map(option => (
                        <ToggleButton key={option.value} sx={{textTransform: 'none'}} value={option.value}>
                            {t(option.label)}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </PanelField>

            <PanelField label={t('Color')}>
                <NewColorPicker
                    disabled={disabled}
                    id="printout-divider-color"
                    onChange={(value) => update({color: value})}
                    placeholder={t('Default')}
                    presetColors={colorPresets}
                    showClear
                    value={color}
                />
            </PanelField>
        </>
    );
}

export default DividerSettings;
